import { useCallback, useEffect, useState } from "react";
import { Ban, Download, FileText, Flag, Loader2, Pencil, Plus, RotateCcw, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY_NOM = { event_id: "", driver_name: "", email: "", phone: "", car_number: "", racing_class: "", racing_class_other: "", transponder: "", licence_no: "", pit_crew: "", notes: "" };

const FALLBACK_CLASSES = ["Formula 500s", "Wingless Sprints", "Street Stocks", "Super Rods", "Junior Quarter Midgets / JQMV", "Production Sedans", "Standard Saloons", "Compact Speedcars", "Lightning Sprints"];

// Classes racing at the selected meeting, always ending with Other
const classesFor = (ev) => {
  const listed = (ev?.classes || "")
    .split("·")
    .map((c) => c.trim())
    .filter((c) => c && !/more classes/i.test(c) && c.toLowerCase() !== "other");
  return [...(listed.length ? listed : FALLBACK_CLASSES), "Other"];
};

export default function AdminNominations({ token }) {
  const [nominations, setNominations] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventFilter, setEventFilter] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_NOM);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const [n, e] = await Promise.all([
        api.get("/admin/nominations", { headers, params: eventFilter ? { event_id: eventFilter } : {} }),
        api.get("/events"),
      ]);
      setNominations(n.data);
      setEvents(e.data);
    } catch {
      toast.error("Failed to load nominations");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, eventFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const selectedEvent = events.find((e) => e.id === form.event_id);
  const classOptions = classesFor(selectedEvent);

  const addNomination = async (e) => {
    e.preventDefault();
    if (!form.event_id || !form.driver_name.trim()) {
      toast.error("Meeting and driver name are required");
      return;
    }
    setSaving(true);
    try {
      const { racing_class_other, ...rest } = form;
      const payload = { ...rest, racing_class: form.racing_class === "Other" ? racing_class_other.trim() || "Other" : form.racing_class };
      await api.post("/admin/nominations", payload, { headers });
      toast.success(`Nomination added for ${form.driver_name}`);
      setForm(EMPTY_NOM);
      setShowAdd(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleWithdraw = async (n) => {
    const next = !n.withdrawn;
    if (next && !window.confirm(`Withdraw ${n.driver_name} from ${n.event_title}? They stay in the list crossed out.`)) return;
    try {
      await api.put(`/admin/nominations/${n.id}/withdraw`, { withdrawn: next }, { headers });
      setNominations((prev) => prev.map((x) => (x.id === n.id ? { ...x, withdrawn: next } : x)));
      toast.success(next ? `${n.driver_name} marked withdrawn` : `${n.driver_name} reinstated`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const [editNom, setEditNom] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_NOM);
  const [editSaving, setEditSaving] = useState(false);

  const openEdit = (n) => {
    const known = classesFor(events.find((e) => e.id === n.event_id));
    const isOther = n.racing_class && !known.includes(n.racing_class);
    setEditForm({
      event_id: n.event_id,
      driver_name: n.driver_name || "",
      email: n.email || "",
      phone: n.phone || "",
      car_number: n.car_number || "",
      racing_class: isOther ? "Other" : n.racing_class || "",
      racing_class_other: isOther ? n.racing_class : "",
      transponder: n.transponder || "",
      licence_no: n.licence_no || "",
      pit_crew: n.pit_crew || "",
      notes: n.notes || "",
    });
    setEditNom(n);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.driver_name.trim()) {
      toast.error("Driver name is required");
      return;
    }
    setEditSaving(true);
    try {
      const { racing_class_other, ...rest } = editForm;
      const payload = { ...rest, racing_class: editForm.racing_class === "Other" ? racing_class_other.trim() || "Other" : editForm.racing_class };
      await api.put(`/admin/nominations/${editNom.id}`, payload, { headers });
      toast.success(`Nomination updated for ${editForm.driver_name}`);
      setEditNom(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setEditSaving(false);
    }
  };

  const remove = async (n) => {
    if (!window.confirm(`Delete nomination for ${n.driver_name}?`)) return;
    try {
      await api.delete(`/admin/nominations/${n.id}`, { headers });
      toast.success("Nomination deleted");
      setNominations((prev) => prev.filter((x) => x.id !== n.id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/nominations/export.csv", {
        headers,
        params: eventFilter ? { event_id: eventFilter } : {},
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "nominations.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Sign-on sheet downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  const downloadSignonPdf = async () => {
    if (!eventFilter) {
      toast.error("Choose a meeting from the dropdown first — the sign-on sheet is per meeting");
      return;
    }
    try {
      const res = await api.get("/admin/nominations/signon.pdf", {
        headers,
        params: { event_id: eventFilter },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sign-on-sheet.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Printable sign-on sheet downloaded — ready for the pit gate");
    } catch {
      toast.error("Couldn't build the sign-on sheet");
    }
  };

  const selectCls =
    "bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-2.5 text-sm transition-colors";

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10 space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-display font-extrabold uppercase text-3xl tracking-tight flex items-center gap-3">
          <Flag className="w-7 h-7 text-clay-orange" /> Nominations
          <span className="text-clay-muted text-xl">({nominations.length})</span>
        </h2>
        <div className="flex flex-wrap items-center gap-3">
          <button
            data-testid="nomination-add-toggle"
            onClick={() => setShowAdd((s) => !s)}
            className="flex items-center gap-2 bg-clay-orange hover:bg-clay-gold text-clay-bg font-mono text-xs uppercase tracking-[0.2em] px-4 py-2.5 transition-colors"
          >
            {showAdd ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showAdd ? "Close" : "Add Nomination"}
          </button>
          <button
            data-testid="nominations-signon-pdf-button"
            onClick={downloadSignonPdf}
            className="flex items-center gap-2 bg-clay-orange hover:bg-clay-gold text-clay-bg font-mono text-xs uppercase tracking-[0.2em] px-4 py-2.5 transition-colors"
          >
            <FileText className="w-4 h-4" /> Sign-On Sheet PDF
          </button>
          <button
            data-testid="nominations-export-button"
            onClick={exportCsv}
            className="flex items-center gap-2 border border-clay-border hover:border-clay-orange font-mono text-xs uppercase tracking-[0.2em] px-4 py-2.5 transition-colors"
          >
            <Download className="w-4 h-4" /> CSV
          </button>
        </div>
      </div>
      <select
        data-testid="nominations-event-filter"
        value={eventFilter}
        onChange={(e) => setEventFilter(e.target.value)}
        className={`${selectCls} w-full sm:max-w-md mt-4`}
      >
        <option value="">All meetings</option>
        {events.map((e) => (
          <option key={e.id} value={e.id}>
            {new Date(`${e.date}T00:00:00`).toLocaleDateString("en-AU", { day: "2-digit", month: "short" })} — {e.title}
          </option>
        ))}
      </select>

      {showAdd && (
        <form
          data-testid="nomination-add-form"
          onSubmit={addNomination}
          className="border border-clay-border bg-clay-card p-6 sm:p-8"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay-muted mb-5">
            Manual entry — for trackside sign-ups and late entries. Works even after nominations close. No emails are sent.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <select data-testid="nomination-event-select" value={form.event_id} onChange={set("event_id")} className={selectCls} required>
              <option value="">Meeting * — choose one</option>
              {events.map((e) => (
                <option key={e.id} value={e.id}>
                  {new Date(`${e.date}T00:00:00`).toLocaleDateString("en-AU", { day: "2-digit", month: "short" })} — {e.title}
                </option>
              ))}
            </select>
            <input data-testid="nomination-driver-input" placeholder="Driver name *" value={form.driver_name} onChange={set("driver_name")} className={selectCls} required />
            <input data-testid="nomination-car-input" placeholder="Car number" value={form.car_number} onChange={set("car_number")} className={selectCls} />
            <select data-testid="nomination-class-select" value={form.racing_class} onChange={set("racing_class")} className={selectCls}>
              <option value="">Class — select *</option>
              {classOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            {form.racing_class === "Other" && (
              <input data-testid="nomination-class-other-input" placeholder="Class — type it in *" value={form.racing_class_other} onChange={set("racing_class_other")} className={selectCls} />
            )}
            <input data-testid="nomination-email-input" type="email" placeholder="Email (optional)" value={form.email} onChange={set("email")} className={selectCls} />
            <input data-testid="nomination-phone-input" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} className={selectCls} />
            <input data-testid="nomination-transponder-input" placeholder="Transponder" value={form.transponder} onChange={set("transponder")} className={selectCls} />
            <input data-testid="nomination-licence-input" placeholder="Licence no." value={form.licence_no} onChange={set("licence_no")} className={selectCls} />
            <input data-testid="nomination-pitcrew-input" placeholder="Pit crew" value={form.pit_crew} onChange={set("pit_crew")} className={selectCls} />
          </div>
          <input data-testid="nomination-notes-input" placeholder="Notes (optional)" value={form.notes} onChange={set("notes")} className={`${selectCls} w-full mt-4`} />
          <button
            data-testid="nomination-save-button"
            disabled={saving}
            className="mt-5 bg-clay-orange hover:bg-clay-gold disabled:opacity-60 text-clay-bg font-display font-bold uppercase tracking-wider px-8 py-3.5 transition-colors duration-300 flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add Nomination
          </button>
        </form>
      )}

      <div className="border border-clay-border overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="border-b border-clay-border bg-clay-surface font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted text-left">
              <th className="px-4 py-3">Meeting</th>
              <th className="px-4 py-3">Driver</th>
              <th className="px-4 py-3">Car #</th>
              <th className="px-4 py-3">Class</th>
              <th className="px-4 py-3">Transponder</th>
              <th className="px-4 py-3">Body / Licence</th>
              <th className="px-4 py-3">Pit Crew</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {nominations.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center text-clay-muted font-mono text-xs uppercase tracking-[0.2em]">
                  No nominations yet — share the Nominate Now button
                </td>
              </tr>
            )}
            {nominations.map((n) => (
              <tr key={n.id} data-testid="nomination-row" className={`border-b border-clay-border/50 hover:bg-clay-card/60 transition-colors ${n.withdrawn ? "opacity-55" : ""}`}>
                <td className="px-4 py-3">
                  <span className={`font-medium ${n.withdrawn ? "line-through" : ""}`}>{n.event_title}</span>
                  <span className="block font-mono text-[10px] text-clay-muted">{n.event_date}</span>
                </td>
                <td className="px-4 py-3 font-medium">
                  <span className={n.withdrawn ? "line-through" : ""}>{n.driver_name}</span>
                  {n.withdrawn && (
                    <span data-testid="nomination-withdrawn-badge" className="ml-2 font-mono text-[9px] uppercase tracking-[0.15em] border border-red-500/50 text-red-400 px-1.5 py-0.5">
                      Withdrawn
                    </span>
                  )}
                  {n.notes && <span className="block text-xs text-clay-muted truncate max-w-[160px]">{n.notes}</span>}
                </td>
                <td className={`px-4 py-3 font-mono text-clay-gold ${n.withdrawn ? "line-through" : ""}`}>{n.car_number}</td>
                <td className={`px-4 py-3 text-clay-muted ${n.withdrawn ? "line-through" : ""}`}>{n.racing_class}</td>
                <td className="px-4 py-3 text-clay-muted font-mono text-xs">{n.transponder || "—"}</td>
                <td className="px-4 py-3">
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] border border-clay-border px-2 py-0.5">{n.sanctioning_body}</span>
                  <span className="block text-xs text-clay-muted mt-1">{n.licence_no || "—"}</span>
                </td>
                <td className="px-4 py-3 text-clay-muted text-xs max-w-[140px] truncate">{n.pit_crew || "—"}</td>
                <td className="px-4 py-3 text-clay-muted text-xs">
                  {n.email}
                  <span className="block">{n.phone}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <button
                      data-testid="nomination-edit-button"
                      onClick={() => openEdit(n)}
                      className="text-clay-muted hover:text-clay-gold transition-colors p-1.5"
                      aria-label="Edit nomination"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid="nomination-withdraw-button"
                      onClick={() => toggleWithdraw(n)}
                      className={`transition-colors p-1.5 ${n.withdrawn ? "text-green-400/80 hover:text-green-300" : "text-clay-muted hover:text-amber-400"}`}
                      aria-label={n.withdrawn ? "Reinstate nomination" : "Withdraw nomination"}
                    >
                      {n.withdrawn ? <RotateCcw className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                    </button>
                    <button
                      data-testid="nomination-delete-button"
                      onClick={() => remove(n)}
                      className="text-clay-muted hover:text-red-400 transition-colors p-1.5"
                      aria-label="Delete nomination"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editNom && (
        <div data-testid="nomination-edit-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-8 overflow-y-auto" onClick={() => setEditNom(null)}>
          <form
            data-testid="nomination-edit-form"
            onSubmit={saveEdit}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-3xl border border-clay-border bg-clay-card p-6 sm:p-8 my-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display font-bold uppercase tracking-tight text-xl text-clay-orange">
                Edit Nomination — {editNom.event_title} ({editNom.event_date})
              </h3>
              <button type="button" data-testid="nomination-edit-close" onClick={() => setEditNom(null)} className="text-clay-muted hover:text-clay-text p-1" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <input data-testid="edit-driver-input" placeholder="Driver name *" value={editForm.driver_name} onChange={(e) => setEditForm((f) => ({ ...f, driver_name: e.target.value }))} className={selectCls} required />
              <input data-testid="edit-car-input" placeholder="Car number" value={editForm.car_number} onChange={(e) => setEditForm((f) => ({ ...f, car_number: e.target.value }))} className={selectCls} />
              <select data-testid="edit-class-select" value={editForm.racing_class} onChange={(e) => setEditForm((f) => ({ ...f, racing_class: e.target.value }))} className={selectCls}>
                <option value="">Class — select *</option>
                {classesFor(editNom ? events.find((ev) => ev.id === editNom.event_id) : null).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              {editForm.racing_class === "Other" && (
                <input data-testid="edit-class-other-input" placeholder="Class — type it in *" value={editForm.racing_class_other} onChange={(e) => setEditForm((f) => ({ ...f, racing_class_other: e.target.value }))} className={selectCls} />
              )}
              <input data-testid="edit-email-input" type="email" placeholder="Email (optional)" value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} className={selectCls} />
              <input data-testid="edit-phone-input" placeholder="Phone (optional)" value={editForm.phone} onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))} className={selectCls} />
              <input data-testid="edit-transponder-input" placeholder="Transponder" value={editForm.transponder} onChange={(e) => setEditForm((f) => ({ ...f, transponder: e.target.value }))} className={selectCls} />
              <input data-testid="edit-licence-input" placeholder="Licence no." value={editForm.licence_no} onChange={(e) => setEditForm((f) => ({ ...f, licence_no: e.target.value }))} className={selectCls} />
              <input data-testid="edit-pitcrew-input" placeholder="Pit crew" value={editForm.pit_crew} onChange={(e) => setEditForm((f) => ({ ...f, pit_crew: e.target.value }))} className={selectCls} />
            </div>
            <input data-testid="edit-notes-input" placeholder="Notes (optional)" value={editForm.notes} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))} className={`${selectCls} w-full mt-4`} />
            <button
              data-testid="edit-save-button"
              disabled={editSaving}
              className="mt-5 bg-clay-orange hover:bg-clay-gold disabled:opacity-60 text-clay-bg font-display font-bold uppercase tracking-wider px-8 py-3.5 transition-colors duration-300 flex items-center gap-2"
            >
              {editSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pencil className="w-4 h-4" />}
              Save Changes
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
